import { CoreSelectionNamespace } from '@sonar-software/sonar-core';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import { useApiResource } from './useApiResource';
import { useGlobalSelect } from './useGlobalSelect';

import { KeyPaginationQueryParams, SonarObjectType } from '@sonar-software/sonar-ts-types';
import { useCallback, useMemo, useRef } from 'react';

type SelectionRecord = Record<string, unknown>;

export type UseSelectionTableOptions<TSelectionRecord extends SelectionRecord> = {
	getRowKey: (record: TSelectionRecord) => number;
	namespace?: string;
	queryParams?: Record<string, unknown> & KeyPaginationQueryParams & KeyPaginationQueryParams;
	resourceIdsUri?: string;
	results?: TSelectionRecord[];
	selectionType?: SonarObjectType;
};

export interface UseSelectionTableResult<TSelectionRecord extends SelectionRecord> {
	handleTablePageSelectAll: (selected: boolean, records: SelectionRecord[]) => void;
	handleTableRowSelect: (record: TSelectionRecord, selected: boolean) => void;
	pageSelectedKeys: number[];
	refreshIds: () => void;
	resetSelection: () => void;
	selectAll: () => void;
	selectedCount: number;
	selectedKeys: number[];
	selectionType?: SonarObjectType;
	totalCount?: number;
	updateSelection: (rowKeys: number[], isSelected?: boolean) => void;
}

export function useSelectionTable<TSelectionRecord extends SelectionRecord>(
	options: UseSelectionTableOptions<TSelectionRecord>
): UseSelectionTableResult<TSelectionRecord> {
	const { getRowKey, namespace, queryParams, resourceIdsUri, results = [], selectionType } = options;
	const { addSelection, getGlobalSelection, removeSelection } = useGlobalSelect();
	const previousResultsRef = useRef<TSelectionRecord[]>();
	const pauseApiCalls = Boolean(!results.length) || results === previousResultsRef.current;

	previousResultsRef.current = results;

	let filteredParams = queryParams;

	if (queryParams) {
		const { count, paginationDirection: direction, key, paginationDirection, key: pageKey, ...rest } = queryParams;

		filteredParams = rest;
	}

	const { request, resource } = useApiResource<{ childSonarObjectIds?: number[]; sonarObjectIds?: number[] }>(
		resourceIdsUri,
		filteredParams,
		pauseApiCalls
	);

	const allResourceIds = resource?.childSonarObjectIds ?? resource?.sonarObjectIds;

	const globalSelection = useMemo(() => getGlobalSelection({ namespace }), [ getGlobalSelection, namespace ]);

	const resultsMap = useMemo<Record<string, TSelectionRecord>>(() => {
		const mapById: Record<string, TSelectionRecord> = {};

		for (const record of results) {
			mapById[ getRowKey(record) ] = record;
		}

		return mapById;
	}, [ getRowKey, results ]);

	const selectedKeys = intersection(allResourceIds, globalSelection);
	const pageSelectedKeys = intersection(selectedKeys, results?.map(getRowKey));

	const updateSelection = useCallback(
		(rowKeys: number[], isSelected?: boolean) => {
			const selected = isSelected ?? true;
			const newSelectedRowKeys = selected ? union(selectedKeys, rowKeys) : difference(selectedKeys, rowKeys);

			if (newSelectedRowKeys.length !== selectedKeys.length) {
				if (selected) {
					const selections: CoreSelectionNamespace = {};

					for (const id of rowKeys) {
						selections[ Number(id) ] = {
							sonarObjectType: (resultsMap[ id ]?.sonarObjectType ?? selectionType) as SonarObjectType
						};
					}

					addSelection({
						namespace,
						selections
					});
				} else {
					removeSelection({
						namespace,
						selections: rowKeys
					});
				}
			}
		},
		[ addSelection, namespace, removeSelection, resultsMap, selectedKeys, selectionType ]
	);

	const resetSelection = useCallback(() => {
		updateSelection(selectedKeys ?? [], false);
	}, [ selectedKeys, updateSelection ]);

	const selectAll = useCallback(() => {
		updateSelection(allResourceIds ?? [], true);
	}, [ allResourceIds, updateSelection ]);

	const handleTableRowSelect = useCallback(
		(record: TSelectionRecord, isSelected: boolean) => {
			updateSelection([ getRowKey(record) ], isSelected);
		},
		[ getRowKey, updateSelection ]
	);

	const handleTablePageSelectAll = useCallback(
		(selected: boolean) => {
			const pageRecords = results ?? [];
			const selection = pageRecords.map(getRowKey);

			updateSelection(selection, selected);
		},
		[ getRowKey, results, updateSelection ]
	);

	return {
		handleTablePageSelectAll,
		handleTableRowSelect,
		pageSelectedKeys,
		refreshIds: request,
		resetSelection,
		selectAll,
		selectedCount: selectedKeys.length,
		selectedKeys,
		totalCount: allResourceIds?.length ?? 0,
		updateSelection
	};
}
